import {
  Alert,
  Button,
  Checkbox,
  Icon,
  InputField,
  SelectField,
} from '@design-system';
import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { COUNTRY_CODES } from '../../../../constants/locations';
import {
  GeneratedAddress,
  GeneratedCreateOrUpdateAddressInput,
  GeneratedOrderDetailsFragment,
} from '../../../../types/generated';
import { customsCodeIsRequired } from '../../../../utils/address';

type FormValues = GeneratedCreateOrUpdateAddressInput;

interface AddressFormProfileAddProps {
  variant: 'ADD_ADDRESS';
  error: boolean;
  loading: boolean;
  onCreate: (input: FormValues) => Promise<string | undefined>;
  onCancel: () => void;
}

interface AddressFormProfileEditProps {
  variant: 'EDIT_ADDRESS';
  address: GeneratedAddress;
  error: boolean;
  loading: boolean;
  onDelete: (id: string) => Promise<void>;
  onUpdate: (id: string, input: FormValues) => Promise<void>;
}

interface AddressFormOrderProps {
  variant: 'UPDATE_SHIPPING';
  address: GeneratedOrderDetailsFragment['shippingAddress'] | GeneratedAddress;
  error: boolean;
  loading: boolean;
  onUpdate: (input: FormValues) => Promise<void>;
  onCancel: () => void;
}

type AddressFormProps =
  | AddressFormProfileAddProps
  | AddressFormProfileEditProps
  | AddressFormOrderProps;

const getDefaultValues = (props: AddressFormProps) => {
  if (props.variant === 'ADD_ADDRESS') {
    return {
      addressLine1: '',
      addressLine2: '',
      city: '',
      company: '',
      countryCode: '',
      countryName: '',
      customsCode: '',
      firstName: '',
      isDefault: true,
      lastName: '',
      phone: '',
      provinceName: '',
      zip: '',
    };
  }

  const { address, variant } = props;

  return {
    addressLine1: address?.addressLine1 ?? '',
    addressLine2: address?.addressLine2 ?? '',
    city: address?.city ?? '',
    company: address?.company ?? '',
    countryCode: address?.countryCode ?? '',
    countryName:
      variant !== 'UPDATE_SHIPPING' ? (address.countryName ?? '') : '',
    customsCode:
      variant !== 'UPDATE_SHIPPING' ? (address.customsCode ?? '') : '',
    firstName: address?.firstName ?? '',
    isDefault: variant === 'EDIT_ADDRESS' ? address.isDefault : true,
    lastName: address?.lastName ?? '',
    phone: address?.phone ?? '',
    provinceName:
      variant !== 'UPDATE_SHIPPING' ? (address?.provinceName ?? '') : '',
    zip: address?.zip ?? '',
  };
};

export const AddressForm = (props: AddressFormProps) => {
  const { t } = useTranslation(['countries', 'addresses']);
  const { error, loading, variant } = props;
  const defaultValues = getDefaultValues(props);

  const form = useForm<FormValues>({
    defaultValues,
  });

  const countryCode = form.watch('countryCode');
  const showCustomsCode = customsCodeIsRequired(countryCode || undefined);

  useEffect(() => {
    if (!showCustomsCode) {
      form.unregister('customsCode');
    }
  }, [form, showCustomsCode]);

  useEffect(() => {
    form.setValue('countryName', t(`countries:${countryCode}`));
  }, [countryCode, form, t]);

  const handleSubmit = async (input: FormValues) => {
    if (variant === 'EDIT_ADDRESS') {
      const { address, onUpdate } = props;

      onUpdate(address.id, input);
    }

    if (variant === 'ADD_ADDRESS') {
      const { onCreate } = props;

      const success = await onCreate(input);
      if (success) form.reset();
    }

    if (variant === 'UPDATE_SHIPPING') {
      const { onUpdate } = props;

      await onUpdate(input);
    }
  };

  const handleDelete = (addressId: string) => {
    if (variant === 'EDIT_ADDRESS') {
      const { onDelete } = props;

      onDelete(addressId);
    }
  };

  return (
    <form
      className="flex flex-col gap-6"
      onSubmit={(ev) => void form.handleSubmit(handleSubmit)(ev)}
    >
      <div className="flex flex-col gap-4">
        {error && <Alert status="error">{t('addresses:dialog.error')}</Alert>}
        <div className="grid w-full grid-cols-2 gap-3">
          <InputField
            key="firstName"
            {...form.register('firstName', {
              required: {
                message: t('addresses:dialog.firstName.validation'),
                value: true,
              },
            })}
            error={form.formState.errors.firstName?.message}
            id="firstName"
            label={t('addresses:dialog.firstName.label')}
          />
          <InputField
            key="lastName"
            {...form.register('lastName', {
              required: {
                message: t('addresses:dialog.lastName.validation'),
                value: true,
              },
            })}
            error={form.formState.errors.lastName?.message}
            id="lastName"
            label={t('addresses:dialog.lastName.label')}
          />
        </div>
        <InputField
          {...form.register('company')}
          id="company"
          label={t('addresses:dialog.companyName.label')}
        />
        <InputField
          {...form.register('addressLine1', {
            required: {
              value: true,
              message: t('addresses:dialog.streetAndHousenumber.validation'),
            },
          })}
          error={form.formState.errors.addressLine1?.message}
          id="addressLine1"
          label={t('addresses:dialog.streetAndHousenumber.label')}
        />
        <InputField
          {...form.register('addressLine2')}
          id="addressLine2"
          label={t('addresses:dialog.apartmentNumber.label')}
        />
        <div className="grid grid-cols-2 gap-x-3">
          <InputField
            {...form.register('zip', {
              required: {
                value: true,
                message: t('addresses:dialog.postalCode.validation'),
              },
            })}
            error={form.formState.errors.zip?.message}
            id="zip"
            label={t('addresses:dialog.postalCode.label')}
          />
          <InputField
            {...form.register('city', {
              required: {
                value: true,
                message: t('addresses:dialog.city.validation'),
              },
            })}
            error={form.formState.errors.city?.message}
            id="city"
            label={t('addresses:dialog.city.label')}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <SelectField
            {...form.register('countryCode', {
              required: {
                value: true,
                message: t('addresses:dialog.countryAndRegion.validation'),
              },
            })}
            disabled={variant === 'UPDATE_SHIPPING'}
            error={form.formState.errors.countryCode?.message}
            id="countryCode"
            label={t('addresses:dialog.countryAndRegion.label')}
            options={COUNTRY_CODES.map((code) => ({
              key: `${t(`countries:${code}`)}`,
              value: code,
            }))}
            placeholder={t('addresses:dialog.countryAndRegion.placeholder')}
          />
          {variant === 'UPDATE_SHIPPING' ? (
            <p className="text-xs text-neutral-4">
              <Trans
                components={{
                  Link: (
                    <Link
                      className="hyperlink"
                      href="mailto:collecting@avantarte.com"
                    />
                  ),
                }}
                i18nKey="addresses:dialog.noteCountry"
                t={t}
              />{' '}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <InputField
            {...form.register('phone', {
              required: {
                value: true,
                message: t('addresses:dialog.phoneNumber.validation'),
              },
            })}
            error={form.formState.errors.phone?.message}
            id="phone"
            label={t('addresses:dialog.phoneNumber.label')}
            prefix={<Icon name="ic_call" />}
          />
          <p className="text-xs text-neutral-4">{t('addresses:dialog.note')}</p>
        </div>
        {showCustomsCode && (
          <div>
            <InputField
              {...form.register('customsCode', {
                required: true,
              })}
              error={form.formState.errors.customsCode?.type}
              hideErrorMessage
              id="customsCode"
              label={t(
                `product:drawers.shipping.customs.labels.${countryCode}`,
              )}
              name="customsCode"
            />
            <p className="mt-1.5 text-xs text-neutral-4">
              {t('product:drawers.review.draw.customs.text', {
                country: `${t(`countries:${countryCode}`)}`,
                label: t(
                  `product:drawers.shipping.customs.labels.${countryCode}`,
                ),
              })}
            </p>
          </div>
        )}
      </div>

      {
        // this rule doesn't play well with discriminated union
        // eslint-disable-next-line react/destructuring-assignment
        !(variant === 'EDIT_ADDRESS' && props.address.isDefault) && (
          <Checkbox
            checked={Boolean(form.watch('isDefault'))}
            id="defineDefaultAddress"
            label={t('addresses:dialog.saveAsDefault')}
            name="defineDefaultAddress"
            onChange={(e) =>
              form.setValue('isDefault', e.currentTarget.checked)
            }
            type="checkbox"
            value="isDefault"
          />
        )
      }

      <div className="flex flex-col gap-3">
        <Button
          disabled={loading}
          fullWidth
          label={t('addresses:dialog.cta.buttonSave')}
          loading={loading}
          type="submit"
          variant="primary"
        />
        {variant === 'EDIT_ADDRESS' ? (
          <Button
            disabled={loading}
            label={t('addresses:dialog.cta.buttonDelete')}
            loading={loading}
            // this rule doesn't play well with discriminated union
            // eslint-disable-next-line react/destructuring-assignment
            onClick={() => handleDelete(props.address.id)}
            variant="secondary"
          />
        ) : null}

        {(variant === 'ADD_ADDRESS' || variant === 'UPDATE_SHIPPING') &&
        // this rule doesn't play well with discriminated union
        // eslint-disable-next-line react/destructuring-assignment
        props.onCancel !== undefined ? (
          <Button
            disabled={loading}
            label={t('addresses:dialog.cta.buttonCancel')}
            loading={loading}
            // this rule doesn't play well with discriminated union
            // eslint-disable-next-line react/destructuring-assignment
            onClick={() => props.onCancel()}
            variant="secondary"
          />
        ) : null}
      </div>
    </form>
  );
};
